import React, { useEffect, useRef, type ReactNode } from 'react';
import { useTracking } from '@de/providers/TrackingProvider';
import {
  isDefaultOnHostPolicy,
  requiresExplicitNodeEnable,
} from '@de/providers/TrackingHostContext';
import { useIntersect } from '@de/hooks/useIntersect';
import { useResultsPagePopupVisibility } from '@de/providers/ResultsPageConfigProvider';
import {
  __resetForTests as __resetSharedFiredKeysForTests,
  addFiredKey,
  hasFiredKey,
} from '@de/utils/firedEntryEventKeys';
import { buildStepCorrelationOverride } from './buildStepCorrelationOverride';
import {
  mergeProductCustomDimensions,
  type ProductIdentityContext,
} from './buildProductIdentity';
import { buildHostElementEventOverride } from '@de/utils/configurable-step/resolveElementFields';
import { buildHostFieldEventContext } from '@de/utils/configurable-step/hostFieldContract';
import { eventCatalog } from '@highereducation/eventing/catalog';
import {
  checkBinding,
  composeConfiguredEvent,
  hasLifecycleBinding,
  readBindings,
  readDefaults,
  resolveBindingField,
  type BindingDefaults,
  type BindingExperience,
  type BindingFieldSource,
  type BindingResolveScope,
  type EventBinding,
} from '@de/utils/configured-events/index';
import type { EventContext } from '@de/utils/createEventingPayloadsDE';
import { shouldRecordLifecycleDedupe } from '@de/utils/hostEventTransport';
import { newRelicNoticeError } from '@de/utils/newRelicActions';
import {
  ensureHostElementCorrelation,
  mintHostElementLifecycleCorrelation,
  resetHostElementCorrelationsForTests,
} from '@de/utils/hostElementCorrelation';

/**
 * Tracking intent declared on a DSL component instance via the
 * `_meta.tracking.*` prop family. The binding enhancer collects every
 * `_meta.*` key into the `meta` payload handed to {@link Trackable}
 * (see `createBoundRender`); a parallel `_tracking.*` collection is
 * not yet wired, so today authors declare tracking intent under
 * `_meta.tracking.*` and we read it from there.
 */
export type TrackingKind = 'element' | 'product' | 'field' | 'submit';

export interface TrackingMeta {
  /**
   * Selects which event family fires. When omitted, defaults to
   * `'element'` so any DSL node wrapped in a Trackable contributes a
   * single `ElementViewed` on intersect — no payload assumptions made.
   */
  readonly kind?: TrackingKind;
  /**
   * Optional explicit `customDimensions.ctaText` for `ProductClicked`
   * (multi-CTA entries). Falls back to the wrapped component's own
   * `ctaText` / `children` when omitted by the author.
   */
  readonly ctaText?: string;
  /**
   * Optional `actionOutcome` override (`'dexp-question' | 'dexp-results'`).
   * When omitted the inherited `eventContext.actionOutcome` is used —
   * which is already resolved per step by `resolveActionOutcome`.
   */
  readonly actionOutcome?: string;
  /**
   * Field metadata for `_tracking.kind === 'field'`. `fieldType` is
   * required by the field-event payload builder; `fieldName` /
   * `leadFieldAssociated` fall back to the surrounding step metadata
   * when omitted.
   */
  readonly fieldType?: string;
  readonly fieldName?: string;
  readonly leadFieldAssociated?: string;
  /**
   * Outbound URL captured on `ProductClicked` for multi-CTA entries.
   */
  readonly outboundUrl?: string;
  /**
   * Optional component identity fields for `kind: 'element'` (and
   * surfaced as `product.location` for `kind: 'product'`). When
   * supplied here they surface on the `ElementViewed` payload via
   * `createStepElementViewedPayload`'s existing override slots, AND
   * on `ProductViewed.product.location` for the `product` branch.
   * Without these, the element branch falls back to step-shaped
   * fields (`htmlId = currentStep.id`, `name = currentStep.title`).
   */
  readonly name?: string;
  readonly elementType?: string;
  readonly htmlId?: string;
  readonly location?: string;
  readonly text?: string;
  /**
   * Optional affiliate / entry-surface product identity for
   * `kind: 'product'` when no repeater `productContext` is present
   * (e.g. DSL `StudentLoansBanner`). Surfaced on `ProductLoaded` /
   * `ProductViewed` / `ProductClicked` wire payloads via
   * {@link buildProductOverride}.
   */
  readonly formatType?: string;
  readonly formatSubtype?: string;
  /**
   * Product `name` for affiliate / entry-surface units where no
   * repeater `productContext` supplies it (e.g. `StudentLoansBanner`,
   * `StudentLoansInline`). Disambiguates units of the same `formatType`
   * in Cohesion reporting.
   */
  readonly productName?: string;
  /**
   * Product `variant` for affiliate / entry-surface units. Maps to the
   * Tagular product object `variant` field. Use to capture creative type
   * (`'banner'` / `'inline'` / `'text-link'`) so Data can slice events
   * by format without relying solely on `location`.
   */
  readonly productVariant?: string;
  /**
   * Element field map. Registered-default hosts resolve these
   * against `host.page`; flow hosts ignore them.
   */
  readonly fields?: Readonly<Record<string, unknown>>;
  /**
   * Increment-two custom dimensions as an array or object map.
   */
  readonly customDimensions?: unknown;
  /**
   * Per-instance event on/off. Its meaning depends on the host policy
   * and the declared `kind`:
   *
   * - `flow-defaults` hosts, `kind: 'product'`: optional off-switch.
   *   When omitted, registry `kind: 'product'` still means events are
   *   on. Explicit `false` suppresses ProductLoaded / ProductViewed /
   *   ProductClicked and does not fall back to ElementViewed. Other
   *   kinds ignore the flag — it is not a general kill switch.
   * - `explicit-node-enable` hosts, `kind: 'element'`: required
   *   opt-in. Only `true` emits ElementViewed / ElementClicked; every
   *   other node stays silent.
   * - `registered-defaults` hosts, `kind: 'element' | 'field'`: opt-out.
   *   Omitted or `true` emits; explicit `false` suppresses lifecycle
   *   and committed Field events.
   *
   * Lives on `_meta.tracking` only. Do not put on/off inside
   * `productFields`.
   */
  readonly enabled?: boolean;
  /**
   * Author bindings. `mount` and `view` are sent from this component.
   * Click handler names stay with the binding enhancer.
   */
  readonly events?: readonly EventBinding[];
  /**
   * Per-moment keep or suppress. `mount` and `view` skip this
   * component's lifecycle events and still send bindings. Click
   * handler names stay with the binding enhancer.
   */
  readonly defaults?: BindingDefaults;
}

const TRACKING_META_KEY = 'tracking';

/**
 * Shape passed in from `createBoundRender` — the binding enhancer
 * has already filtered `_meta.*` props down to a string-keyed map.
 * `Trackable` reads `meta.tracking` (typed as {@link TrackingMeta})
 * and ignores everything else.
 */
export interface TrackableProps {
  readonly meta?: Readonly<Record<string, unknown>>;
  /**
   * Stable per-DSL-instance identifier used as the **primary**
   * fire-once dedup key. When supplied (createBoundRender threads
   * the Puck node id, scoped by dslConfigId + stepIndex), the
   * WeakSet-keyed-on-DOM-node dedup is bypassed in favour of a
   * Set-keyed-on-string dedup that survives DOM remounts.
   *
   * Why this matters: every time a parent re-keys, an async data
   * fetch resolves, or React Strict Mode does its mount/unmount/
   * remount cycle, the wrapped component is unmounted and remounted
   * with a brand-new DOM node. The WeakSet-keyed-on-DOM dedup never
   * sees the new node, so it lets a duplicate FieldViewed through.
   * The stableKey is the same across all those remounts within a
   * single provider session (it's derived from the DSL data tree
   * plus the per-session `flowSessionId`, not from React lifecycle),
   * so the Set catches the duplicate before it fires.
   *
   * `undefined` falls back to the legacy WeakSet path so callers
   * that have no stable identifier (e.g. legacy non-DSL render
   * paths, integration tests) still get the original semantics.
   */
  readonly stableKey?: string;
  /**
   * Per-card product identity (`brand` / `presets` / `productId` / `sku`
   * / `position` / `viewCorrelationId` / `listId` + format + location
   * defaults) derived from the enclosing repeater item by
   * `createBoundRender` for `kind: 'product'` nodes. Merged into the
   * `ProductLoaded` / `ProductViewed` override so DSL result cards reach
   * product parity with the imperative `Card` without per-config
   * `_meta.tracking` authoring. Ignored for non-product
   * kinds; the author's `_meta.tracking.location` still overrides the
   * `location` default.
   */
  readonly productContext?: ProductIdentityContext;
  // PII-sanitized view-time value for `kind: 'field'` nodes (resolved by
  // `createBoundRender`); threaded onto `FieldViewed.fieldValue`.
  readonly fieldValue?: string;
  /**
   * Fields whose options come from a query stay quiet until that
   * query has settled and the control has its initial value.
   */
  readonly fieldViewReady?: boolean;
  readonly elementContext?: Readonly<Record<string, unknown>>;
  /**
   * Click listener on the active wrapper, for a click that only carries
   * tracking. Clicks bubble to it from the wrapped component, so child
   * handlers run first and a stopped click never reaches it.
   */
  readonly onClick?: React.MouseEventHandler<HTMLSpanElement>;
  /** Puck node id stamped on a configured event as `blockId`. */
  readonly blockId?: string;
  /**
   * Node id already passed to `composeDedupKey` for the out-of-the-box
   * key. Mount and view bindings call it again with a binding identity.
   */
  readonly dedupNodeId?: string;
  readonly bindingScope?: BindingResolveScope;
  readonly children: ReactNode;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readTrackingMeta = (
  meta: Readonly<Record<string, unknown>> | undefined,
): TrackingMeta | null => {
  if (!meta) return null;
  const entry = meta[TRACKING_META_KEY];
  if (!isPlainObject(entry)) return null;
  return entry as TrackingMeta;
};

/**
 * True when a tracking slice is Product-kind and explicitly turned off.
 *
 * Unset / non-boolean `enabled` is not off — registry Product defaults
 * stay on. Non-product kinds always return false so this flag cannot
 * disable Field or Element tracking.
 *
 * @param {TrackingMeta | Readonly<Record<string, unknown>> | null | undefined} tracking
 * Tracking slice from `_meta.tracking` or a merged equivalent.
 * @returns {boolean} Whether Product events must be suppressed for this placement.
 */
export function isProductTrackingDisabled(
  tracking: TrackingMeta | Readonly<Record<string, unknown>> | null | undefined,
): boolean {
  if (tracking == null || typeof tracking !== 'object') return false;
  return tracking.kind === 'product' && tracking.enabled === false;
}

interface LifecycleBindingIds {
  readonly blockId?: string;
  readonly dedupNodeId?: string;
}

/**
 * The fire-once key for one lifecycle binding. The send path and the
 * observer's settled check must read the same key, or a node either
 * re-observes forever or skips a binding. A binding with no node id
 * has no key: the binding id is shared across nodes, so it cannot
 * stand in for one.
 */
function lifecycleBindingKey(
  tracking: NonNullable<ReturnType<typeof useTracking>>,
  ids: LifecycleBindingIds,
  binding: EventBinding,
): string | undefined {
  const nodeId = ids.dedupNodeId ?? ids.blockId;
  if (!nodeId) return undefined;
  return tracking.composeDedupKey(nodeId, {
    blockId: ids.blockId ?? nodeId,
    eventBindingId: binding.id,
  });
}

function resolveFieldMap(
  sources: Readonly<Record<string, BindingFieldSource>> | undefined,
  scope: BindingResolveScope,
): Record<string, string | undefined> {
  const resolved: Record<string, string | undefined> = {};
  if (!sources) return resolved;
  for (const key of Object.keys(sources)) {
    const source = sources[key];
    if (source) resolved[key] = resolveBindingField(source, scope);
  }
  return resolved;
}

/**
 * Sends one moment's bindings after the out-of-the-box event for that
 * moment. A catalog drop is skipped. A throw from composition or send
 * stays inside that binding. The key and the catalog check run first.
 * Returns false when a send was rejected, so the caller can retry
 * without burning the key. Returns true when this moment has no bindings.
 */
function sendLifecycleBindings(
  moment: 'mount' | 'view',
  tracking: NonNullable<ReturnType<typeof useTracking>>,
  trackingMeta: TrackingMeta,
  ids: LifecycleBindingIds,
  bindingScope: BindingResolveScope | undefined,
): boolean {
  const bindings = readBindings(trackingMeta).filter(
    (binding) => binding.on === moment,
  );
  if (bindings.length === 0) return true;
  const experience: BindingExperience = {
    ...(bindingScope?.experience ?? tracking.configuredEventContext ?? {}),
    ...(ids.blockId ? { blockId: ids.blockId } : {}),
  };
  const scope: BindingResolveScope = {
    item: bindingScope?.item,
    sources: bindingScope?.sources ?? {},
    experience,
  };
  const isStepFreeHost =
    requiresExplicitNodeEnable(tracking.host) ||
    isDefaultOnHostPolicy(tracking.host?.policy);
  const eventContext = {
    ...(tracking.eventContext ?? {}),
    ...(isStepFreeHost ? {} : buildStepCorrelationOverride(tracking)),
  } as EventContext;
  let ready = true;
  for (const binding of bindings) {
    const key = lifecycleBindingKey(tracking, ids, binding);
    if (!key || hasFiredKey(key)) continue;
    const checked = checkBinding(binding, moment, eventCatalog);
    if ('reason' in checked) {
      addFiredKey(key);
      continue;
    }
    try {
      const composed = composeConfiguredEvent({
        binding,
        entry: checked.entry,
        resolvedFields: resolveFieldMap(checked.fields, scope),
        resolvedCustomDimensions: resolveFieldMap(
          checked.customDimensions,
          scope,
        ),
        experience,
        eventContext,
        valueSets: eventCatalog.valueSets,
      });
      if ('reason' in composed) {
        addFiredKey(key);
        continue;
      }
      const result = tracking.sendConfiguredEvent(
        binding.event,
        composed.payload,
      );
      if (!shouldRecordLifecycleDedupe(result)) {
        ready = false;
        continue;
      }
      addFiredKey(key);
    } catch (err) {
      // One binding cannot stop the next, or the out-of-the-box event.
      addFiredKey(key);
      newRelicNoticeError(err instanceof Error ? err : new Error(String(err)), {
        source: `trackable:configured-event:${binding.id}`,
      });
    }
  }
  return ready;
}

/**
 * Primary fire-once dedup keyed by a stable, DSL-derived string id
 * (Puck node id + dslConfigId + stepIndex + flowSessionId — see
 * `createBoundRender`).
 *
 * The shared `@de/utils/firedEntryEventKeys` module hosts the
 * underlying Set so the DSL render path's entry-point firer
 * (`useEntryPointEventFiring`) and `Trackable` gate on the same
 * primitive. The two consumers use distinct key namespaces
 * (`Trackable`: Puck-node-id-shaped keys; entry-point firer:
 * `entryPoint:<eventName>:<scope>:<stepId>:<flowSessionId>`) so
 * they cannot collide on a key even though they share storage.
 *
 * Why a string-keyed Set is the *primary* gate (over the
 * DOM-keyed WeakSet directly below):
 *
 * Every Trackable mount produces a *new* observed DOM node —
 * `weakSetHit` is `false` even when the same logical dropdown
 * fires `FieldViewed` multiple times in a single page load. The
 * over-fire vectors are all "unmount + remount with fresh DOM"
 * rather than "second observer on the same DOM node":
 *   - React 18 Strict Mode does a literal mount → unmount → remount
 *     cycle in dev with new component instances and new DOM.
 *   - Hosts keying `<DynamicExperienceProvider>` on a value that
 *     transitions during async flow fetch destroy the entire
 *     provider tree and remount it with new DOM.
 *   - Data-source cascades flip provider state and any conditional
 *     ancestor wrapper (loading skeletons, suspense boundaries,
 *     parent re-keys) detaches the subtree.
 *
 * The DOM-keyed WeakSet is fundamentally the wrong dedup key here:
 * the DOM is being recreated *as part of the over-fire vector*.
 * The stable key derived from the DSL tree (which doesn't churn
 * on remount) is the correct dedup identity.
 *
 * Scope: per package-instance, per provider-mount session. A fresh
 * page load (or a navigation that unmounts the package-loaded
 * module) starts with an empty Set; a fresh provider mount in the
 * same tab rotates `flowSessionId` so prior-session stamps no
 * longer match. Both are the right behaviour — those *are* fresh
 * fire opportunities. Payload-level dedup (Cohesion-side
 * idempotency) remains out of scope.
 */

/**
 * Defense-in-depth secondary dedup keyed by the **observed DOM node**.
 *
 * Retained alongside the primary `FIRED_STABLE_KEYS` Set for two
 * reasons:
 *   1. Trackable can be mounted by callers that don't supply a
 *      `stableKey` (legacy non-DSL render paths, ad-hoc test
 *      mounts). For those callers the WeakSet remains the only
 *      defense and preserves the prior "fire once per observed
 *      node" semantic.
 *   2. When both gates are present, the WeakSet still catches
 *      duplicate intersect callbacks emitted by a single observer
 *      on a single node before the entries handler can disconnect.
 *      Cheap, GC-safe, no behavioural cost.
 *
 * Entries are GC'd with their Element when the DOM node is removed.
 */
const FIRED_NODES = new WeakSet<Element>();

/**
 * Fire-once dedup for the mount-time `ProductLoaded` event emitted by
 * `kind: 'product'` Trackables. Separate from the intersect-driven
 * view-event Sets because `ProductLoaded` fires on **mount**
 * (visibility-independent) — mirroring the imperative `Card`, which
 * emits `ProductLoaded` from a `useEffect(…, [])` and `ProductViewed`
 * on intersect. Keyed on the stable, DSL-derived id so a Strict Mode
 * double-mount / parent re-key / async-fetch remount can't double-emit.
 * Callers with no `stableKey` fall back to mount-once semantics (the
 * same posture the imperative `Card`'s empty-deps mount effect has).
 */
const FIRED_PRODUCT_LOADED_KEYS = new Set<string>();

/**
 * Test-only escape hatch — every module-level dedup set must be
 * cleared between unit-test renders so each test starts with the
 * "never fired" precondition. Production code MUST NOT call this.
 *
 * Delegates to the shared `@de/utils/firedEntryEventKeys` reset so
 * any entry-point key stamped by the DSL render path's firer
 * (`useEntryPointEventFiring`) is also cleared. Tests that need to
 * reset both consumers in a single call still go through this
 * helper.
 */
/**
 * Consumes the field view for one logical node. A host input that
 * creates the first value calls this so a later remount cannot emit
 * FieldViewed after FieldInputted. The key is the same stable id
 * Trackable records when a view actually fires.
 */
export function markHostFieldViewConsumed(stableKey: string | undefined): void {
  if (stableKey) addFiredKey(stableKey);
}

export function __resetTrackableDedupForTests(): void {
  FIRED_PRODUCT_LOADED_KEYS.clear();
  __resetSharedFiredKeysForTests();
  resetHostElementCorrelationsForTests();
  // WeakSet has no `clear()` API by design (entries are weakly
  // held), but tests that exercise the WeakSet branch always
  // mount fresh DOM nodes per render, so the previous-test
  // entries become unreachable and GC-eligible immediately —
  // there is no observable cross-test leakage.
}

/**
 * `Trackable` — the analytics wrapper that replaces
 * {@link TrackablePlaceholder} on the DSL render path.
 *
 * Behaviour:
 * - Mounts a layout-neutral `<span data-edu-trackable="active">` that
 *   carries the wrapped subtree. The span is observed by an
 *   `IntersectionObserver`; the first intersection above the
 *   threshold fires `ElementViewed` (or `ProductViewed` /
 *   `FieldViewed` based on `meta.tracking.kind`) **once per observed
 *   DOM node** and then disconnects, mirroring the standard-path
 *   {@link useViewEvents} contract.
 * - Reads correlation IDs and trigger functions from
 *   {@link useTracking}. When mounted outside a `<TrackingProvider>`
 *   (e.g. in legacy / placeholder tests) `Trackable` no-ops so the
 *   binding-enhancer harness keeps rendering bound components in
 *   isolation.
 * - DSL components themselves remain pure presentational —
 *   `Trackable` is the only place that reads `eventContext` and
 *   calls trigger functions for **view** events. Field-input events
 *   (FieldInputted) are dispatched alongside the formKey capture in
 *   `createBoundRender` so the user's value is available at the
 *   firing site.
 *
 * Carries `data-edu-trackable="active"` so integration tests can
 * assert the real wrapper is mounted (vs. the placeholder, which
 * carries `="placeholder"`).
 */
export const Trackable: React.FC<TrackableProps> = ({
  meta,
  stableKey,
  productContext,
  fieldValue,
  fieldViewReady = true,
  elementContext,
  onClick,
  blockId,
  dedupNodeId,
  bindingScope,
  children,
}) => {
  const tracking = useTracking();
  const trackingMeta = readTrackingMeta(meta);
  const isExplicitHost = requiresExplicitNodeEnable(tracking?.host);
  const isDefaultOnHost = isDefaultOnHostPolicy(tracking?.host?.policy);
  const isDisabledExplicitNode =
    isExplicitHost &&
    (trackingMeta?.kind !== 'element' || trackingMeta.enabled !== true);
  const isDisabledDefaultOnNode =
    isDefaultOnHost &&
    (trackingMeta?.kind == null ||
      !['element', 'field'].includes(trackingMeta.kind) ||
      trackingMeta.enabled === false);

  const continueForBindings =
    tracking != null &&
    trackingMeta != null &&
    hasLifecycleBinding(trackingMeta) &&
    (isDisabledExplicitNode ||
      isDisabledDefaultOnNode ||
      isProductTrackingDisabled(trackingMeta));

  if (
    (isDisabledExplicitNode || isDisabledDefaultOnNode) &&
    !continueForBindings
  ) {
    // Lifecycle stays off. A configured click still needs this listener.
    if (onClick) {
      return (
        <span
          data-edu-trackable='placeholder'
          onClick={onClick}
          style={{ display: 'contents' }}
        >
          {children}
        </span>
      );
    }
    return <>{children}</>;
  }

  // Product off is resolved before the Element default (`kind ?? 'element'`).
  // Translating off to `kind: 'element'` would silently emit ElementViewed.
  if (isProductTrackingDisabled(trackingMeta) && !continueForBindings) {
    // A configured click still listens here. Product view and click stay off.
    if (onClick) {
      return (
        <span
          data-edu-trackable='placeholder'
          onClick={onClick}
          style={{ display: 'contents' }}
        >
          {children}
        </span>
      );
    }
    const otherKeys = Object.keys(meta ?? {}).filter(
      (key) => key !== TRACKING_META_KEY,
    );
    if (otherKeys.length === 0) {
      return <>{children}</>;
    }
    return (
      <span
        data-edu-trackable='placeholder'
        data-edu-meta-keys={otherKeys.join(',')}
        style={{ display: 'contents' }}
      >
        {children}
      </span>
    );
  }

  // Backward compatibility with `TrackablePlaceholder`: when the
  // resolved `_meta.*` map has entries but no `tracking` slice, fall
  // back to the placeholder's marker shape so any DSL test suite that
  // already asserts `data-edu-trackable` keeps green and the DOM
  // signature stays stable across the placeholder → real swap.
  //
  // Hook discipline note: `ActiveTrackable` mounts an
  // `IntersectionObserver` and calls trigger functions; we only need
  // those when tracking is actually configured. Splitting on the
  // outer keeps the placeholder + pass-through paths from paying
  // any observer cost (and keeps jsdom-style tests that have no
  // `IntersectionObserver` polyfill green).
  if (!trackingMeta || !tracking) {
    // Product tracking that is turned off arrives with the slice removed.
    // The click listener still belongs here so a configured binding can fire.
    if (onClick) {
      return (
        <span
          data-edu-trackable='placeholder'
          onClick={onClick}
          style={{ display: 'contents' }}
        >
          {children}
        </span>
      );
    }
    const hasMeta = meta !== undefined && Object.keys(meta).length > 0;
    if (!hasMeta) {
      return <>{children}</>;
    }
    return (
      <span
        data-edu-trackable='placeholder'
        data-edu-meta-keys={Object.keys(meta ?? {}).join(',')}
        style={{ display: 'contents' }}
      >
        {children}
      </span>
    );
  }

  return (
    <ActiveTrackable
      key={stableKey}
      tracking={tracking}
      trackingMeta={trackingMeta}
      stableKey={stableKey}
      productContext={productContext}
      fieldValue={fieldValue}
      fieldViewReady={fieldViewReady}
      elementContext={elementContext}
      onClick={onClick}
      blockId={blockId}
      dedupNodeId={dedupNodeId}
      bindingScope={bindingScope}
      suppressLifecycle={continueForBindings}
    >
      {children}
    </ActiveTrackable>
  );
};

interface ActiveTrackableProps {
  readonly tracking: NonNullable<ReturnType<typeof useTracking>>;
  readonly trackingMeta: TrackingMeta;
  readonly stableKey?: string;
  readonly productContext?: ProductIdentityContext;
  readonly fieldValue?: string;
  readonly fieldViewReady?: boolean;
  readonly elementContext?: Readonly<Record<string, unknown>>;
  readonly onClick?: React.MouseEventHandler<HTMLSpanElement>;
  readonly blockId?: string;
  readonly dedupNodeId?: string;
  readonly bindingScope?: BindingResolveScope;
  readonly suppressLifecycle: boolean;
  readonly children: ReactNode;
}

const HOST_TRANSPORT_RETRY_EVENT = 'OneTrustGroupsUpdated';

function retainRejectedHostLifecycle(attempt: () => boolean): () => void {
  let retryTimer: number | undefined;
  let retained = true;

  const cleanup = () => {
    retained = false;
    if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    window.removeEventListener(HOST_TRANSPORT_RETRY_EVENT, schedule);
  };
  const retry = () => {
    retryTimer = undefined;
    if (retained && attempt()) cleanup();
  };
  const schedule = () => {
    if (!retained || retryTimer !== undefined) return;
    retryTimer = window.setTimeout(retry, 0);
  };

  window.addEventListener(HOST_TRANSPORT_RETRY_EVENT, schedule);
  schedule();
  return cleanup;
}

/**
 * Inner component that owns the IntersectionObserver wiring. Only
 * mounts when both a `<TrackingProvider>` is present and the resolved
 * meta declares a `tracking` slice — see {@link Trackable} for the
 * gating rationale.
 *
 * Fire-once contract: backed by the module-level `FIRED_NODES`
 * WeakSet (keyed on the observed DOM node), so even if the React
 * instance is unmounted and re-mounted on top of the same DOM
 * element (Strict Mode double-effect, DSM-driven parent re-render
 * cascade, Radix re-key) the underlying node still fires its view
 * event exactly once.
 */
const ActiveTrackable: React.FC<ActiveTrackableProps> = ({
  tracking,
  trackingMeta,
  stableKey,
  productContext,
  fieldValue,
  fieldViewReady = true,
  elementContext,
  onClick,
  blockId,
  dedupNodeId,
  bindingScope,
  suppressLifecycle,
  children,
}) => {
  const { setNodes, entries, disconnect } = useIntersect(
    tracking.host?.viewThreshold ?? 1,
  );
  const ref = useRef<HTMLSpanElement>(null);
  const loadedWithoutStableKey = useRef(false);
  const kind = trackingMeta.kind ?? 'element';

  // A results-page Recommender / Spotlight popup occludes
  // the entire background DSL tree, but `IntersectionObserver` is
  // purely geometric and reports the hidden cards as "intersecting"
  // anyway. Firing a view event here would emit `ProductViewed`
  // against an occluded card and permanently burn the fire-once dedup
  // budget, so the card never re-fires once the popup is dismissed.
  // Gate observation on the popup being closed; when it closes the
  // observe effect re-runs and attaches a fresh observer so the now-
  // visible card fires on its genuine intersect. Defaults to `false`
  // (no gating) outside a results page — see `useResultsPagePopupVisibility`.
  const { isPopupOpen } = useResultsPagePopupVisibility();

  // `kind: 'product'` reaches product-event parity with the imperative
  // `Card`, which emits `ProductLoaded` on mount (visibility-
  // independent) in addition to the on-intersect `ProductViewed`. Fire
  // it here so DSL result cards land `ProductLoaded` on the wire —
  // carrying `dslConfigId` via the StepCorrelation override — without
  // the DSL author writing any event code. Deduped on the stable key so
  // a Strict Mode / re-key remount doesn't double-emit; mount-once
  // otherwise (parity with the imperative `Card`'s empty-deps effect).
  const bindingIds: LifecycleBindingIds = {
    blockId,
    dedupNodeId,
  };
  const lifecycleDefaults = readDefaults(trackingMeta);
  const suppressMount = lifecycleDefaults.mount === 'suppress';
  const suppressView = lifecycleDefaults.view === 'suppress';
  const productMountOwns =
    !suppressLifecycle &&
    kind === 'product' &&
    !requiresExplicitNodeEnable(tracking.host);
  const hostElementMountOwns =
    !suppressLifecycle &&
    isDefaultOnHostPolicy(tracking.host?.policy) &&
    kind === 'element';

  useEffect(() => {
    if (!productMountOwns) return;
    const attempt = () => {
      if (!(stableKey && FIRED_PRODUCT_LOADED_KEYS.has(stableKey))) {
        if (stableKey) FIRED_PRODUCT_LOADED_KEYS.add(stableKey);
        if (!suppressMount) {
          fireProductLoadedEvent({ tracking, trackingMeta, productContext });
        }
      }
      return sendLifecycleBindings(
        'mount',
        tracking,
        trackingMeta,
        bindingIds,
        bindingScope,
      );
    };
    if (attempt()) return;
    return retainRejectedHostLifecycle(attempt);
    // Mount-once: `tracking` / `trackingMeta` are stable for a given
    // step render; the binding enhancer rebuilds `meta` per render, so
    // including them here would re-emit on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (productMountOwns || hostElementMountOwns) return;
    const attempt = () =>
      sendLifecycleBindings(
        'mount',
        tracking,
        trackingMeta,
        bindingIds,
        bindingScope,
      );
    if (attempt()) return;
    return retainRejectedHostLifecycle(attempt);
    // Mount-once for nodes whose kind has no out-of-the-box mount event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hostElementMountOwns) return;
    const loadedKey = stableKey ? `host-loaded:${stableKey}` : undefined;
    const attemptLoaded = () => {
      const alreadyLoaded =
        (loadedKey && hasFiredKey(loadedKey)) ||
        (!loadedKey && loadedWithoutStableKey.current);
      if (!alreadyLoaded) {
        if (!suppressMount) {
          const correlationId = ensureHostElementCorrelation(stableKey);
          const loadedResult = tracking.triggers.triggerElementLoadedEvent({
            ...buildHostElementEventOverride(
              trackingMeta,
              buildHostResolveContext(tracking.host.page, elementContext),
            ),
            correlationId,
          });
          if (!shouldRecordLifecycleDedupe(loadedResult)) return false;
        }
        if (loadedKey) {
          addFiredKey(loadedKey);
        } else {
          loadedWithoutStableKey.current = true;
        }
      }
      return sendLifecycleBindings(
        'mount',
        tracking,
        trackingMeta,
        bindingIds,
        bindingScope,
      );
    };

    if (attemptLoaded()) return;
    return retainRejectedHostLifecycle(attemptLoaded);
    // Mount-once for a logical node. The stable-key registry handles remounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewBindingsSettled = (): boolean =>
    readBindings(trackingMeta).every((binding) => {
      if (binding.on !== 'view') return true;
      const key = lifecycleBindingKey(tracking, bindingIds, binding);
      return key === undefined || hasFiredKey(key);
    });

  useEffect(() => {
    if (!ref.current) return;
    // The wrapper carries `display: contents` so it stays neutral
    // inside flex / grid parents (a real `inline` / `block` span
    // would become a phantom flex item and shift surrounding
    // layout). The trade-off: a `display: contents` element has no
    // box of its own — IntersectionObserver still fires entries for
    // it, but every entry's `boundingClientRect` collapses to 0×0
    // and `isIntersecting` reports `false` permanently across all
    // major engines. Observe the first rendered child element
    // instead so the observer attaches to a real layout box; fall
    // back to the wrapper only when the subtree is text-only or
    // empty (no DSL component renders that shape today, but the
    // fallback keeps the contract safe).
    const target =
      ref.current.firstElementChild instanceof Element
        ? ref.current.firstElementChild
        : ref.current;

    // Primary dedup: stable-key Set. Catches the over-fire vector
    // where a parent re-keys / Strict Mode remounts / async fetch
    // resolution destroys+recreates the DOM node — the new node is
    // unknown to the WeakSet, but the stable key identifies the
    // same logical Trackable across all those remounts.
    // Defense-in-depth: WeakSet on the observed node. Catches
    // duplicate intersect callbacks on the same node before the
    // entries handler can disconnect, and remains the only defense
    // for callers that don't supply a stableKey.
    if (stableKey && hasFiredKey(stableKey) && viewBindingsSettled()) return;
    if (!stableKey && FIRED_NODES.has(target) && viewBindingsSettled()) return;
    // While a blocking popup occludes this subtree, clear the observed
    // set instead of observing — the card isn't actually visible.
    // `setNodes([])` tears down any observer attached on a prior render
    // (e.g. before the popup auto-opened) without the permanent stop
    // `disconnect()` would impose, so the effect can re-attach once the
    // popup closes (`isPopupOpen` is in the dep array).
    if (isPopupOpen) {
      setNodes([]);
      return;
    }
    setNodes([target]);
  }, [setNodes, kind, stableKey, isPopupOpen]);

  useEffect(() => {
    if (entries.length === 0) return;
    // Defense-in-depth against the mount race: the observer may already
    // be attached (and report an intersection) in the brief window
    // before the popup's auto-open propagates here. Never fire / dedup
    // while the popup occludes the card — the observe effect clears the
    // observer and re-attaches once `isPopupOpen` flips back to false.
    if (isPopupOpen) return;
    if (kind === 'field' && !fieldViewReady) return;
    const intersecting = entries.find((entry) => entry.isIntersecting);
    if (!intersecting) return;

    const target = intersecting.target;
    if (
      (stableKey ? hasFiredKey(stableKey) : FIRED_NODES.has(target)) &&
      viewBindingsSettled()
    ) {
      disconnect();
      return;
    }

    const attemptView = () => {
      const ootbRecorded = stableKey
        ? hasFiredKey(stableKey)
        : FIRED_NODES.has(target);
      const skipView = suppressLifecycle || suppressView;
      const recordView = () => {
        if (stableKey) addFiredKey(stableKey);
        FIRED_NODES.add(target);
      };
      if (!skipView && !ootbRecorded) {
        const viewResult = fireViewEvent({
          kind,
          tracking,
          trackingMeta,
          stableKey,
          elementContext,
          productContext,
          fieldValue,
        });
        if (!shouldRecordLifecycleDedupe(viewResult)) return false;
        recordView();
      } else if (skipView && !ootbRecorded) {
        recordView();
      }
      if (
        !sendLifecycleBindings(
          'view',
          tracking,
          trackingMeta,
          bindingIds,
          bindingScope,
        )
      ) {
        return false;
      }
      disconnect();
      return true;
    };

    if (attemptView()) return;
    // A flow host records its view even when the trigger reports
    // rejected, so this branch is not a second chance for that view.
    // The timer runs when a binding was rejected after the view was
    // recorded, or on a default-on host.
    const ootbViewSettled = stableKey
      ? hasFiredKey(stableKey)
      : FIRED_NODES.has(target);
    if (isDefaultOnHostPolicy(tracking.host?.policy) || ootbViewSettled) {
      return retainRejectedHostLifecycle(attemptView);
    }
    // `entries` is the meaningful trigger. `fieldViewReady` retries a
    // query-backed field view once its options and value have settled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, fieldViewReady]);

  return (
    <span
      ref={ref}
      data-edu-trackable='active'
      data-edu-tracking-kind={kind}
      style={{ display: 'contents' }}
      onClick={onClick}
    >
      {children}
    </span>
  );
};

interface FireViewEventArgs {
  readonly kind: TrackingKind;
  readonly tracking: NonNullable<ReturnType<typeof useTracking>>;
  readonly trackingMeta: TrackingMeta;
  readonly stableKey?: string;
  readonly elementContext?: Readonly<Record<string, unknown>>;
  readonly productContext?: ProductIdentityContext;
  readonly fieldValue?: string;
}

/**
 * Fan out the on-intersect event to the right trigger function based
 * on the declared `kind`. Each branch builds the same partial-context
 * shape consumed by the existing payload helpers in
 * `createEventingPayloadsDE`, then hands off to the trigger function
 * surfaced by {@link useFlowTrackingEvents}. Cohesion validation lives
 * inside those payload builders — this fan-out is intentionally thin.
 *
 * Every branch threads `StepCorrelation` through the override block as
 * `customDimensions` entries (AC #2 — DSL in-flow events carry the
 * per-step correlation on the wire).
 *
 * Note: the `'field'` branch fires `FieldViewed` only. The companion
 * `FieldInputted` event is dispatched from `createBoundRender`'s
 * formKey-capture wrapper — that's the firing site that has the
 * user's value, and centralising both field events there would
 * couple `Trackable` to the binding-enhancer surface.
 */
function fireViewEvent({
  kind,
  tracking,
  trackingMeta,
  stableKey,
  elementContext,
  productContext,
  fieldValue,
}: FireViewEventArgs): unknown {
  if (
    requiresExplicitNodeEnable(tracking.host) ||
    isDefaultOnHostPolicy(tracking.host?.policy)
  ) {
    const { correlationId, viewCorrelationId } =
      mintHostElementLifecycleCorrelation(stableKey);
    const resolveContext = buildHostResolveContext(
      tracking.host.page,
      elementContext,
    );
    const hostOverride = {
      ...tracking.host.page,
      ...buildHostElementEventOverride(trackingMeta, resolveContext),
      correlationId,
    };
    if (kind === 'field') {
      const nodeId =
        typeof elementContext?.id === 'string' ? elementContext.id : undefined;
      const label =
        typeof elementContext?.label === 'string'
          ? elementContext.label
          : undefined;
      return tracking.triggers.triggerFieldEvents('FieldViewed', {
        ...buildHostFieldEventContext({
          page: tracking.host.page,
          tracking: trackingMeta as Readonly<Record<string, unknown>>,
          nodeId,
          label,
          fieldValue,
        }),
        correlationId,
      } as Parameters<typeof tracking.triggers.triggerFieldEvents>[1]);
    }
    return tracking.triggers.triggerElementViewedEvent({
      ...hostOverride,
      ...(viewCorrelationId ? { viewCorrelationId } : {}),
    } as Parameters<typeof tracking.triggers.triggerElementViewedEvent>[0]);
  }

  const { triggers, eventContext } = tracking;
  const stepOverride = buildStepCorrelationOverride(tracking);
  switch (kind) {
    case 'product': {
      // `kind: 'product'` fires `ProductViewed`. The admin contract
      // maps the four authored `_meta.tracking.*` slots (ctaText /
      // outboundUrl / actionOutcome / location) onto the wire payload:
      //   - `location` rides `product.location` via the existing
      //     payload-builder slot.
      //   - `ctaText` / `outboundUrl` / `actionOutcome` ride
      //     `customDimensions` because `createProductViewedPayload`
      //     has no first-class slot for them and admin treats them as
      //     informational rather than product-identity. This keeps
      //     per-component identity (name / variant / category)
      //     inheritance intact (entry-level product wins) while still
      //     surfacing the click-affordance metadata on the wire.
      const override = buildProductOverride(
        stepOverride,
        trackingMeta,
        productContext,
      );
      triggers.triggerProductViewedEvent(
        override as Parameters<typeof triggers.triggerProductViewedEvent>[0],
      );
      return;
    }
    case 'field': {
      // `triggerFieldEvents` is typed for a full `EventContext`, but
      // `createFieldEventsPayload` only reads optional fields; the
      // assertion narrows our merged Partial-shaped object to satisfy
      // the trigger's signature without erasing the inherited keys.
      const merged = {
        ...(eventContext ?? {}),
        ...stepOverride,
        fieldName: trackingMeta.fieldName,
        fieldType: trackingMeta.fieldType,
        fieldId: trackingMeta.leadFieldAssociated,
        fieldValue,
      } as Parameters<typeof triggers.triggerFieldEvents>[1];
      triggers.triggerFieldEvents('FieldViewed', merged);
      return;
    }
    case 'submit':
      // `kind: 'submit'` is intentionally a no-op on both the view
      // and click branches.
      //
      // - View (here): `submit` is a click-driven contract; firing an
      //   intersect event would advertise misleading semantics on the
      //   wire.
      // - Click: there is no dedicated `SubmitClicked` dispatcher.
      //   Submit CTAs route their click through `_action.onClick`
      //   (e.g. `flow.forward`) into `handleStepForward`, and the
      //   provider-owned `useForwardStepEventFiring` fires the
      //   spec-compliant `FormStarted` / `ProductClicked` /
      //   `FormContinued` triple per Confluence "Dynamic Experience
      //   Eventing Requirements" §6.1 / §6.7. Keeping the `'submit'`
      //   kind on the type preserves backwards compatibility with
      //   DSL configs in the wild that still author it.
      return;
    case 'element':
    default: {
      const elementOverride: Record<string, unknown> = { ...stepOverride };
      if (trackingMeta.name !== undefined) {
        elementOverride.name = trackingMeta.name;
      }
      if (trackingMeta.elementType !== undefined) {
        elementOverride.elementType = trackingMeta.elementType;
      }
      if (trackingMeta.htmlId !== undefined) {
        elementOverride.htmlId = trackingMeta.htmlId;
      }
      if (trackingMeta.location !== undefined) {
        elementOverride.location = trackingMeta.location;
      }
      if (trackingMeta.text !== undefined) {
        elementOverride.text = trackingMeta.text;
      }
      triggers.triggerElementViewedEvent(
        elementOverride as Parameters<
          typeof triggers.triggerElementViewedEvent
        >[0],
      );
      return;
    }
  }
}

function buildHostResolveContext(
  hostPage: Readonly<Record<string, unknown>>,
  elementContext: Readonly<Record<string, unknown>> | undefined,
): Readonly<Record<string, unknown>> {
  if (!elementContext) return hostPage;
  const labelCandidates = [
    elementContext.name,
    elementContext.title,
    elementContext.label,
  ];
  const name = labelCandidates.find(
    (value): value is string =>
      typeof value === 'string' && value.trim().length > 0,
  );
  return {
    ...hostPage,
    ...elementContext,
    ...(name ? { name } : {}),
    ...(elementContext.filterFormat === undefined &&
    typeof elementContext.variant === 'string'
      ? { filterFormat: elementContext.variant }
      : {}),
  };
}

/**
 * Build the partial event-context override shared by the product
 * lifecycle emitters — `ProductLoaded` (on mount) and `ProductViewed`
 * (on intersect): StepCorrelation `customDimensions` (incl.
 * `dslConfigId`) plus the authored `ctaText` / `outboundUrl` /
 * `actionOutcome` extras and optional product identity/location fields.
 * Extracted so both events ride an identical override — the admin contract
 * treats them as the same product surface, only the event name differs.
 */
function buildProductOverride(
  stepOverride: ReturnType<typeof buildStepCorrelationOverride>,
  trackingMeta: TrackingMeta,
  productContext?: ProductIdentityContext,
): Record<string, unknown> {
  // Fold per-card identity dims (e.g. `resultsSource`) in via the shared merge
  // so they survive the explicit `customDimensions` key.
  const extraDims = collectProductCustomDimensions(trackingMeta);
  const customDimensions = mergeProductCustomDimensions(
    stepOverride.customDimensions,
    productContext,
    extraDims,
  );
  return {
    // Per-card product identity (brand / presets / productId / sku /
    // position / correlation + format + location defaults) derived from
    // the repeater item. Spread first so the authored
    // `_meta.tracking.location` below still wins, and so the
    // StepCorrelation `customDimensions` (which carry `dslConfigId`)
    // are never clobbered by the identity payload. Config overlay
    // rides `mappedCustomDimensions` on productContext through this
    // spread so payload builders can merge it onto platform dims.
    ...(productContext ?? {}),
    ...(stepOverride.dslConfigId
      ? { dslConfigId: stepOverride.dslConfigId }
      : {}),
    ...(stepOverride.dslConfigVersion !== undefined
      ? { dslConfigVersion: stepOverride.dslConfigVersion }
      : {}),
    ...(trackingMeta.formatType !== undefined
      ? { formatType: trackingMeta.formatType }
      : {}),
    ...(trackingMeta.formatSubtype !== undefined
      ? { formatSubtype: trackingMeta.formatSubtype }
      : {}),
    ...(trackingMeta.location !== undefined
      ? { location: trackingMeta.location }
      : {}),
    ...(trackingMeta.productName !== undefined ||
    trackingMeta.productVariant !== undefined
      ? {
          product: {
            ...(trackingMeta.productName !== undefined
              ? { name: trackingMeta.productName }
              : {}),
            ...(trackingMeta.productVariant !== undefined
              ? { variant: trackingMeta.productVariant }
              : {}),
          },
        }
      : {}),
    customDimensions,
  };
}

/**
 * Emit `ProductLoaded` on mount for `kind: 'product'`, reaching product
 * parity with the imperative `Card` (which fires `ProductLoaded` from a
 * mount effect, independent of visibility). The companion
 * `ProductViewed` fires from {@link fireViewEvent} on intersect. Both
 * ride the same {@link buildProductOverride} shape so `dslConfigId` and
 * the authored product extras land on the `ProductLoaded` wire too.
 */
function fireProductLoadedEvent({
  tracking,
  trackingMeta,
  productContext,
}: {
  readonly tracking: NonNullable<ReturnType<typeof useTracking>>;
  readonly trackingMeta: TrackingMeta;
  readonly productContext?: ProductIdentityContext;
}): void {
  if (requiresExplicitNodeEnable(tracking.host)) return;
  const { triggers } = tracking;
  const stepOverride = buildStepCorrelationOverride(tracking);
  const override = buildProductOverride(
    stepOverride,
    trackingMeta,
    productContext,
  );
  triggers.triggerProductLoadedEvent(
    override as Parameters<typeof triggers.triggerProductLoadedEvent>[0],
  );
}

/**
 * Build the per-component customDimensions entries that ride
 * alongside StepCorrelation for `kind: 'product'`. Admin's contract
 * surfaces `ctaText` / `outboundUrl` / `actionOutcome` here (not on
 * `product.*`) so the entry-level product identity stays intact and
 * Cohesion reports keyed off product fields aren't polluted with
 * per-CTA values. Skipped (returns `[]`) when the author authored
 * none of them — keeps the wire payload clean for the default case.
 */
function collectProductCustomDimensions(
  trackingMeta: TrackingMeta,
): Array<{ key: string; value: string }> {
  const out: Array<{ key: string; value: string }> = [];
  if (trackingMeta.ctaText) {
    out.push({ key: 'ctaText', value: trackingMeta.ctaText });
  }
  if (trackingMeta.outboundUrl) {
    out.push({ key: 'outboundUrl', value: trackingMeta.outboundUrl });
  }
  if (trackingMeta.actionOutcome) {
    out.push({ key: 'actionOutcome', value: trackingMeta.actionOutcome });
  }
  return out;
}

/**
 * HOC variant: `withTrackable(Component)` returns a wrapped component
 * that forwards a `meta` prop into `<Trackable>`. Useful when DSL
 * authors want to attach tracking semantics to a specific HEN UI
 * primitive without writing the wrapper inline.
 */
export function withTrackable<P extends object>(
  Component: React.ComponentType<P>,
): React.FC<P & { meta?: Readonly<Record<string, unknown>> }> {
  const Wrapped: React.FC<P & { meta?: Readonly<Record<string, unknown>> }> = ({
    meta,
    ...rest
  }) => (
    <Trackable meta={meta}>
      <Component {...(rest as unknown as P)} />
    </Trackable>
  );
  Wrapped.displayName = `Trackable(${
    Component.displayName ?? Component.name ?? 'Component'
  })`;
  return Wrapped;
}
